import { branchOrdersRoom, connectRealtimeWhenAvailable, subscribeRooms } from '@beatq/shared';
import { createRealtimeConnectionTicket } from '../merchant-exit/merchantExitApi.js';

export type MintedRealtimeTicket = {
  ticket: string;
  websocketUrl?: string;
};

export type MerchantOpsSocket = NonNullable<
  Awaited<ReturnType<typeof connectRealtimeWhenAvailable>>
>;

/** Parse BE mint envelope: `{ data: { connection: { ticket, websocketUrl } } }`. */
export function parseRealtimeTicketEnvelope(envelope: unknown): MintedRealtimeTicket | null {
  if (envelope == null || typeof envelope !== 'object') return null;
  const root = envelope as Record<string, unknown>;
  const data =
    root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;
  const connection =
    data.connection && typeof data.connection === 'object'
      ? (data.connection as Record<string, unknown>)
      : data;
  const ticket =
    (typeof connection.ticket === 'string' && connection.ticket) ||
    (typeof connection.token === 'string' && connection.token) ||
    null;
  if (!ticket) return null;
  const websocketUrl =
    (typeof connection.websocketUrl === 'string' && connection.websocketUrl) ||
    (typeof connection.url === 'string' && connection.url) ||
    undefined;
  return { ticket, websocketUrl };
}

/** Slow REST fallback while the Socket.IO room is connected. */
export const MERCHANT_LIVE_FALLBACK_POLL_MS = 60_000;

export function merchantOpsPollMs(live: boolean, downMs: number): number {
  return live ? MERCHANT_LIVE_FALLBACK_POLL_MS : downMs;
}

/**
 * Mint a merchant ticket, probe `/live`, connect `/merchant`, subscribe to the
 * branch orders room. Returns null when the gateway is down (poll fallback).
 */
export async function connectMerchantOpsSocket(
  branchId: string,
): Promise<MerchantOpsSocket | null> {
  const envelope = await createRealtimeConnectionTicket({
    purpose: 'merchant-ops',
    branchId,
  });
  const minted = parseRealtimeTicketEnvelope(envelope);
  if (!minted) {
    console.warn('[merchant-realtime] ticket parse failed', envelope);
    return null;
  }
  const socket = await connectRealtimeWhenAvailable({
    surface: 'merchant',
    ticket: minted.ticket,
    ...(minted.websocketUrl ? { url: minted.websocketUrl } : {}),
  });
  if (!socket) return null;
  await subscribeRooms(socket, [branchOrdersRoom(branchId)]);
  return socket;
}

export function bindMerchantSocketLive(
  socket: MerchantOpsSocket,
  onLive: (live: boolean) => void,
): () => void {
  const sync = () => onLive(Boolean(socket.connected));
  socket.on('connect', sync);
  socket.on('disconnect', sync);
  sync();
  return () => {
    socket.off('connect', sync);
    socket.off('disconnect', sync);
  };
}
